import type { AuthError } from '@supabase/supabase-js';

export type AuthErrorCode =
  | 'invalid_credentials'
  | 'user_already_registered'
  | 'email_not_confirmed'
  | 'invalid_email'
  | 'weak_password'
  | 'password_reset_failed'
  | 'magic_link_failed'
  | 'oauth_failed'
  | 'rate_limited'
  | 'network'
  | 'session_expired'
  | 'unknown';

export interface MappedAuthError {
  code: AuthErrorCode;
  message: string;
  rawMessage: string;
  status?: number;
}

export const defaultAuthErrorMessages: Record<AuthErrorCode, string> = {
  invalid_credentials: 'Invalid email or password.',
  user_already_registered: 'An account with this email already exists.',
  email_not_confirmed: 'Please confirm your email before signing in.',
  invalid_email: 'Please enter a valid email address.',
  weak_password: 'Password is too weak. Please choose a stronger password.',
  password_reset_failed: 'Password reset failed. Please request a new link.',
  magic_link_failed: 'Magic link failed. Please request a new link.',
  oauth_failed: 'OAuth sign-in failed. Please try again.',
  rate_limited: 'Too many attempts. Please try again later.',
  network: 'Network error. Please check your connection and try again.',
  session_expired: 'Your session has expired. Please sign in again.',
  unknown: 'Something went wrong. Please try again.',
};

export const mapAuthError = (error?: AuthError | null): MappedAuthError => {
  if (!error) {
    return {
      code: 'unknown',
      message: defaultAuthErrorMessages.unknown,
      rawMessage: '',
    };
  }

  const rawMessage = error.message || '';
  const message = rawMessage.toLowerCase();
  const status = error.status;

  if (message.includes('invalid login credentials') || message.includes('invalid credentials')) {
    return { code: 'invalid_credentials', message: defaultAuthErrorMessages.invalid_credentials, rawMessage, status };
  }

  if (message.includes('user already registered')) {
    return { code: 'user_already_registered', message: defaultAuthErrorMessages.user_already_registered, rawMessage, status };
  }

  if (message.includes('email not confirmed') || message.includes('confirm your email')) {
    return { code: 'email_not_confirmed', message: defaultAuthErrorMessages.email_not_confirmed, rawMessage, status };
  }

  if (message.includes('invalid email') || message.includes('email is invalid')) {
    return { code: 'invalid_email', message: defaultAuthErrorMessages.invalid_email, rawMessage, status };
  }

  if (message.includes('password') && (message.includes('weak') || message.includes('at least') || message.includes('too short'))) {
    return { code: 'weak_password', message: defaultAuthErrorMessages.weak_password, rawMessage, status };
  }

  if (message.includes('rate limit') || status === 429) {
    return { code: 'rate_limited', message: defaultAuthErrorMessages.rate_limited, rawMessage, status };
  }

  if (message.includes('magic link') || message.includes('otp')) {
    return { code: 'magic_link_failed', message: defaultAuthErrorMessages.magic_link_failed, rawMessage, status };
  }

  if (message.includes('oauth') || message.includes('provider')) {
    return { code: 'oauth_failed', message: defaultAuthErrorMessages.oauth_failed, rawMessage, status };
  }

  if (message.includes('refresh token') || (message.includes('session') && message.includes('expired'))) {
    return { code: 'session_expired', message: defaultAuthErrorMessages.session_expired, rawMessage, status };
  }

  if (message.includes('token') || message.includes('expired') || message.includes('reset password')) {
    return { code: 'password_reset_failed', message: defaultAuthErrorMessages.password_reset_failed, rawMessage, status };
  }

  if (message.includes('failed to fetch') || message.includes('network')) {
    return { code: 'network', message: defaultAuthErrorMessages.network, rawMessage, status };
  }

  return { code: 'unknown', message: defaultAuthErrorMessages.unknown, rawMessage, status };
};
